// src/pages/editor/EditTask.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function EditTask() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const { currentTheme } = useContext(ThemeContext);

  const [formData, setFormData] = useState({
    edited_video_link: '',
    editor_notes: '',
    status: 'in_progress',
    progress_notes: ''
  });


  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const editorId = sessionStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE}/get_task_details.php?task_id=${taskId}&editor_id=${editorId}`);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Response: ${text.substring(0, 200)}`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.task) {
        setTask(data.task);
        // Pre-fill form with existing data
        setFormData({
          edited_video_link: data.task.edited_video_link || '',
          editor_notes: data.task.editor_notes || '',
          status: data.task.status || 'in_progress',
          progress_notes: data.task.progress_notes || ''
        });
      } else {
        throw new Error(data.message || 'Task not found');
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      setMessage('❌ Error loading task: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!formData.edited_video_link && formData.status === 'completed') {
    setMessage('❌ Please provide edited video link before marking as completed');
    return;
  }

  setSubmitting(true);
  setMessage('');

  try {
    const editorId = sessionStorage.getItem('userId');
    
    console.log('Submitting to update_task.php:', {
      task_id: parseInt(taskId),
      editor_id: parseInt(editorId),
      ...formData
    });

    // Test if the server is reachable first
    console.log('Testing connection to:', `${API_BASE}/update_task.php`);

    const response = await fetch(`${API_BASE}/update_task.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        task_id: parseInt(taskId),
        editor_id: parseInt(editorId),
        edited_video_link: formData.edited_video_link,
        editor_notes: formData.editor_notes,
        progress_notes: formData.progress_notes,
        status: formData.status
      })
    });

    console.log('Response status:', response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }

    const responseText = await response.text();
    console.log('Raw response:', responseText);
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      throw new Error('Server returned invalid JSON: ' + responseText.substring(0, 100));
    }

    console.log('Parsed response:', data);

    if (data.success) {
      setMessage('✅ Task updated successfully!');
      setTimeout(() => {
        navigate('/my-tasks');
      }, 1500);
    } else {
      setMessage('❌ ' + (data.message || 'Error updating task'));
    }
  } catch (error) {
    console.error('Error updating task:', error);
    
    if (error.message.includes('Failed to fetch')) {
      setMessage('❌ Cannot connect to server. Please check: 1) PHP server is running, 2) Correct API URL, 3) CORS settings');
    } else {
      setMessage('❌ Error: ' + error.message);
    }
  } finally {
    setSubmitting(false);
  }
};  const handleSaveDraft = async () => {
    setSubmitting(true);
    setMessage('');

    try {
      const editorId = sessionStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE}/update_task.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: parseInt(taskId),
          editor_id: parseInt(editorId),
          ...formData,
          status: 'in_progress', // Keep as in progress for drafts
          is_draft: true
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage('✅ Draft saved successfully!');
      } else {
        setMessage('❌ ' + (data.message || 'Error saving draft'));
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setMessage('❌ Error saving draft: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'bg-primary text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      under_review: { class: 'bg-info text-white', text: 'Under Review', icon: 'fa-eye' },
      rejected: { class: 'bg-danger text-white', text: 'Rejected', icon: 'fa-times' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-double' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class}`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const primaryColor = currentTheme?.primary || '#0d6efd';

  if (loading) {
    return (
      <Layout>
        <div className="container-fluid py-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border" style={{ color: primaryColor, width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: primaryColor }}>Loading task details...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!task) {
    return (
      <Layout>
        <div className="container-fluid py-4">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Task not found or you don't have permission to access it.
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/editor/my-tasks')}
          >
            Back to My Tasks
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <button 
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => navigate('/editor/my-tasks')}
                >
                  <i className="fas fa-arrow-left me-1"></i>
                  Back to Tasks
                </button>
                <h1 className="h3 d-inline-block mb-0 ms-2" style={{ color: primaryColor }}>
                  Edit Task: {task.video_name || 'Unnamed Task'}
                </h1>
              </div>
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleSaveDraft}
                  disabled={submitting}
                >
                  <i className="fas fa-save me-1"></i>
                  Save Draft
                </button>
              </div>
            </div>

            {message && (
              <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mb-4`}>
                <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                {message}
                <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
              </div>
            )}

            {/* Only show the submission form - removed task details column */}
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">
                <div className="card shadow-sm border-0">
                  <div className="card-header text-white" style={{ 
                    background: `linear-gradient(135deg, ${primaryColor}, ${currentTheme?.secondary || '#6f42c1'})` 
                  }}>
                    <h5 className="mb-0">
                      <i className="fas fa-edit me-2"></i>
                      Submit Your Work
                    </h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      {/* Edited Video Link */}
                      <div className="mb-3">
                        <label htmlFor="edited_video_link" className="form-label">
                          <strong>Edited Video Link *</strong>
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          id="edited_video_link"
                          name="edited_video_link"
                          value={formData.edited_video_link}
                          onChange={handleInputChange}
                          placeholder="https://drive.google.com/... or https://dropbox.com/..."
                          required={formData.status === 'completed'}
                        />
                        <div className="form-text">
                          Provide a link to your edited video (Google Drive, Dropbox, WeTransfer, etc.)
                        </div>
                      </div>

                      {/* Progress Notes */}
                      <div className="mb-3">
                        <label htmlFor="progress_notes" className="form-label">
                          <strong>Progress Notes</strong>
                        </label>
                        <textarea
                          className="form-control"
                          id="progress_notes"
                          name="progress_notes"
                          rows="3"
                          value={formData.progress_notes}
                          onChange={handleInputChange}
                          placeholder="What did you work on? Any challenges or updates..."
                        ></textarea>
                      </div>

                      {/* Editor Notes */}
                      <div className="mb-4">
                        <label htmlFor="editor_notes" className="form-label">
                          <strong>Final Notes (Optional)</strong>
                        </label>
                        <textarea
                          className="form-control"
                          id="editor_notes"
                          name="editor_notes"
                          rows="4"
                          value={formData.editor_notes}
                          onChange={handleInputChange}
                          placeholder="Any final comments for the reviewer, special instructions, or things to note about your edits..."
                        ></textarea>
                      </div>

                      {/* Status */}
                      <div className="mb-4">
                        <label htmlFor="status" className="form-label">
                          <strong>Update Status</strong>
                        </label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="in_progress">In Progress</option>
                          <option value="under_review">Ready for Review</option>
                          <option value="completed">Completed</option>
                        </select>
                        <div className="form-text">
                          Select "Ready for Review" when you're done and want someone to check your work
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={submitting}
                          style={{
                            background: '#28a745',
                            borderColor: '#28a745'
                          }}
                        >
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-2"></i>
                              Update Task
                            </>
                          )}
                        </button>
                        
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleSaveDraft}
                          disabled={submitting}
                        >
                          <i className="fas fa-save me-2"></i>
                          Save Draft
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-danger ms-auto"
                          onClick={() => navigate('/editor/my-tasks')}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EditTask;